import React, { useState, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./Users.css";
import UserCreatePopup from "./Popup/UserCreatePopup";
import UserModifyPopup from "./Popup/UserModifyPopup";
import * as userFunctions from "../../../functions/Functions";
import { formatAccess, formatDate } from "../../../components/Util/DateFormat";
import Swal from "sweetalert2";
import { getServerBaseUrl } from "../../../utils/portConfig";

function Users() {
  // State 변수들
  const [userData, setUserData] = useState([]); // 사용자 데이터
  const [selectedUser, setSelectedUser] = useState(null); // 선택된 사용자
  const [isPopupOpen, setIsPopupOpen] = useState(false); // 팝업 열림 상태
  const [popupType, setPopupType] = useState(null); // 팝업 종류
  const [gridReload, setGridReload] = useState(false); // 그리드 다시 로드 플래그

  // 테이블 높이 설정값
  const [pageSize, setPageSize] = useState(10); // 기본값 10으로 시작
  const gridContainerRef = useRef(null);

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1/users`;
  const token = localStorage.getItem("token");
  const id = localStorage.getItem("id");
  const userId = localStorage.getItem("userid");

  // 권한 및 사용유무 옵션
  const authOptions = ["superadmin", "admin", "doctor", "nurse"];
  const enableOptions = ["사용", "미사용"];

  // 그리드 참조 변수
  const gridRef = useRef(null);

  // 사용자 유형 플래그
  const userTypeFlag = "user";

  // 사용자 데이터 가져오기 및 그리드 업데이트
  useEffect(() => {
    let fetchDataTimeout;
    const fetchData = async () => {
      // 데이터 로딩 인디케이터 표시
      Swal.fire({
        title: "데이터를 가져오는 중...",
        text: "잠시만 기다려 주세요.",
        didOpen: () => {
          Swal.showLoading();
        },
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
      });

      try {
        // 사용자 데이터 가져오기
        const data = await userFunctions.loadData(`${API_SERVER_URL}/list/${id}`, token);
        setUserData(data);
      } catch (error) {
        console.error("Data fetch error:", error);
        // 데이터 가져오기 오류 처리
        Swal.fire("오류!", "데이터를 가져오는 중 오류가 발생했습니다.", "error");
      } finally {
        // 데이터 가져오기 완료 후 로딩 인디케이터 숨기기
        fetchDataTimeout = setTimeout(() => {
          Swal.close();
        }, 500);
      }
    };

    // 데이터 가져오기 함수 실행
    fetchData();

    // 컴포넌트 언마운트 시 clearTimeout을 통해 이전 setTimeout 핸들러 제거
    return () => {
      clearTimeout(fetchDataTimeout);
    };
  }, [gridReload]);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      cellStyle: { textAlign: "center" },
      flex: 1,
    },
    {
      field: "username",
      headerName: "사용자명",
      cellStyle: { textAlign: "left" },
      flex: 1,
    },
    {
      field: "hospital",
      headerName: "병원",
      cellStyle: { textAlign: "left" },
      valueGetter: (params) => {
        switch (params.data.hospital) {
          case "":
            return "소속병원 없음";
          default:
            return params.data.hospital;
        }
      },
      flex: 1,
    },
    {
      field: "roleId",
      headerName: "권한",
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: authOptions,
      },
      valueGetter: (params) => {
        switch (params.data.roleId) {
          case 1:
            return "시스템관리자";
          case 2:
            return "병원관리자";
          case 3:
            return "의사";
          case 4:
            return "간호사";
          case 5:
            return "환자";
          default:
            return "환자";
        }
      },
      flex: 1,
    },
    {
      field: "enabled",
      headerName: "사용유무",
      cellRenderer: (params) => (params.value ? "사용" : "미사용"),
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: enableOptions,
      },
      flex: 1,
    },
    {
      field: "createdDt",
      headerName: "계정생성일",
      valueFormatter: (params) => formatDate(params.data.createdDt),
      flex: 1,
    },
    {
      field: "id",
      hide: true, // 숨겨진 필드
    },
    {
      field: "hospitalId",
      hide: true, // 숨겨진 필드
    },
  ];

  // 그리드 옵션
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
    };
  }, []);

  // 그리드 행 클릭 시 사용자 정보 가져오기
  const onRowClicked = (event) => {
    Swal.fire({
      title: "데이터를 가져오는 중...",
      text: "잠시만 기다려 주세요.",
      didOpen: async () => {
        Swal.showLoading();

        try {
          // 사용자 상세 정보 가져오기
          const userDetail = await userFunctions.getUserDetail(event.data.links.self.href, token);
          setSelectedUser(userDetail);
          userFunctions.OpenModifyPopup(setIsPopupOpen, setPopupType);
        } catch (error) {
          console.error("Error fetching user detail:", error);
          // 데이터 가져오기 오류 처리
          Swal.fire("오류!", "데이터를 가져오는 중 오류가 발생했습니다.", "error");
        } finally {
          // 데이터 가져오기 완료 후 로딩 인디케이터 숨기기
          setTimeout(() => {
            Swal.close();
          });
        }
      },
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });
  };

  // 팝업 열기 함수
  const OpenCreatePopup = () => {
    userFunctions.OpenCreatePopup(setIsPopupOpen, setPopupType);
  };

  // 팝업 닫기 함수
  const ClosePopup = () => {
    userFunctions.ClosePopup(setIsPopupOpen);
    if (gridRef.current) {
      gridRef.current.api.deselectAll();
      setSelectedUser(null);
    }
  };

  // 사용자 생성 함수
  const createUser = async (userData) => {
    await userFunctions.createUser(API_SERVER_URL, token, userId, userData, setIsPopupOpen, setGridReload);
  };

  // 사용자 수정 함수
  const editUser = async (userData) => {
    await userFunctions.editUser(API_SERVER_URL, token, selectedUser, userData, setIsPopupOpen, setGridReload, setSelectedUser);
  };

  // 사용자 삭제 함수
  const deleteUser = async () => {
    await userFunctions.deleteInfoFunction(selectedUser, token, userTypeFlag, setGridReload, setSelectedUser, setIsPopupOpen);
  };

  // 비밀번호 초기화 함수
  const resetUserPassword = async () => {
    await userFunctions.resetUserPassword(API_SERVER_URL, selectedUser, token, userId, setGridReload, setSelectedUser, setIsPopupOpen);
  };

  // 화면 크기가 변경될 때 테이블 높이를 조정하는 함수
  const calculatePageSize = () => {
    if (gridContainerRef.current) {
      const gridHeight = gridContainerRef.current.clientHeight;
      const rowHeight = 43; // AG Grid의 기본 행 높이 (alpine 테마)
      const headerHeight = 45; // 헤더 높이
      const paginationHeight = 45; // 페이지네이션 아래 여백

      const availableRows = Math.floor((gridHeight - headerHeight - paginationHeight) / rowHeight);

      // 최소 5개, 계산된 값보다 작은 행 수로 설정
      const newPageSize = Math.max(5, availableRows);
      setPageSize(newPageSize);
    }
  };

  // 컴포넌트 마운트 및 창 크기 변경 시 테이블 높이 조정
  useEffect(() => {
    calculatePageSize();

    // 창 크기 변경 이벤트 리스너 추가
    window.addEventListener("resize", calculatePageSize);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("resize", calculatePageSize);
    };
  }, []);

  // 데이터 로드 후 그리드 크기에 맞게 테이블 높이 재계산
  useEffect(() => {
    if (userData.length > 0) {
      setTimeout(calculatePageSize, 100); // 데이터 로드 후 약간의 지연을 두고 계산
    }
  }, [userData]);

  return (
    <div className="dashboard-content">
      <div className="dashboard-content-container">
        <div className="dashboard-content-header">
          <h2>사용자 관리</h2>
        </div>

        <div className="search-container">
          <div className="button-group">
            <button className="dashboard-content-button blue-button" onClick={OpenCreatePopup}>
              생성
            </button>
          </div>
        </div>

        <UserCreatePopup isOpen={isPopupOpen && popupType === "create"} onClose={ClosePopup} onSave={createUser} userBasicData={userData[0]} />

        <UserModifyPopup isOpen={isPopupOpen && popupType === "modify"} onClose={ClosePopup} onSave={editUser} selectedUser={selectedUser} onResetPassword={resetUserPassword} onDelete={deleteUser} />

        <div ref={gridContainerRef} className="ag-theme-alpine" style={{ height: "100%", width: "100%", fontWeight: "400" }}>
          <AgGridReact
            ref={gridRef}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={pageSize} // 동적으로 계산된 테이블 높이 사용
            defaultColDef={defaultColDef}
            rowData={userData}
            onRowClicked={onRowClicked}
            rowSelection="single"
            domLayout="autoHeight"
            multiSortKey="shift"
          />
        </div>
      </div>
    </div>
  );
}

export default Users;
